import React, { useEffect, useState } from 'react'
import { Paper, Group, Button, TextInput } from '@mantine/core'
import StatusSwitch from '../../../entities/components/status-switch'
import { StrategyParams } from 'app/models/strategy-params'
import { useDispatch } from 'react-redux'
import { saveStrategy } from 'app/state/ducks/strategies/thunks'
import { ErrorNotification, SuccessNotification } from 'app/views/components/notifications/notification'
import { useForm } from '@mantine/form'


interface StrategyFormProps {
    newStrategy: StrategyParams | undefined
    setFormExpanded: React.Dispatch<React.SetStateAction<boolean>>
}

const StrategyForm: React.FC<StrategyFormProps> = ({ newStrategy, setFormExpanded }) => {
    const dispatch = useDispatch()
    const [isLoading, setIsLoading] = useState(false)

    const handleCreateStrategy = async () => {
        setIsLoading(true)
        try {
            const validatedForm = form.validate()
            if (validatedForm.hasErrors) return
            const values = { 
                ...form.values,
                code: form.values.code.toUpperCase(),
            }
            form.validate()
            const result: any = await dispatch(saveStrategy(values, values.id));
            if (!result.success) {
                ErrorNotification({ message: result.payload, title: 'Save Strategy Failed' })
                return
            }
            SuccessNotification({
                message: 'The strategy has been successfully saved.',
                title: 'Save Successful',
            })
            setFormExpanded(false)
            form.reset();
        } catch (error) {
            console.log(error)
            ErrorNotification({
                message: 'An error occurred while saving the strategy.',
                title: 'Save Failed',
            });
        } finally {
            setIsLoading(false)
        }
    };

    useEffect(() => {
        if (newStrategy) {
            form.setValues(newStrategy)
        }

    }, [newStrategy]);

    const form: any = useForm({
        initialValues: newStrategy ?? {
            name: '',
            code: '',
            isActive: false
        },
        validate: {
            name: (value) => (value.trim() === '' ? 'Strategy Name is required' : null),
            code: (value) => {
                if (!value.trim()) return 'Strategy Code is required';
                if (!/^[a-zA-Z0-9]{6}$/.test(value)) return 'Strategy Code must be exactly 6 alphanumeric characters';
                return null;
              },
        },
        validateInputOnBlur: true,
        validateInputOnChange: true,
    })

    return (
        <Paper
            p='lg'
            radius='md'
            className='portfolio-strategy-form-paper'
            style={{ borderRadius: newStrategy ? '0px 0px 12px 12px' : '12px' }}
        >
            <form onSubmit={handleCreateStrategy}>
                <Group spacing='lg' mb='md'>
                    <label className='portfolio-strategy-label'>Strategy Name:</label>
                    <TextInput
                        placeholder='Add Strategy Name'
                        className='portfolio-strategy-input'
                        {...form.getInputProps('name')}
                    />
                    <label className='portfolio-strategy-label'>Code:</label>
                    <TextInput
                        placeholder='Add Strategy Code'
                        className='portfolio-strategy-input'
                        maxLength={6}
                        {...form.getInputProps('code')}
                        error={form.errors.code}
                    />
                </Group>

                <Group align='center' mb='md' sx={{ width: '100%' }} position='apart'>
                    <StatusSwitch
                        isActive={form.values.isActive}
                        onToggle={(isActive: boolean) => form.setFieldValue('isActive', isActive)}
                    />
                    <Group className='form-actions-group'>
                        <Button
                            variant='outline'
                            onClick={() => setFormExpanded(false)}
                            className='form-actions-button cancel'
                            loading={isLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleCreateStrategy}
                            className='form-actions-button save'
                            loading={isLoading}
                        >
                            Save
                        </Button>
                    </Group>
                </Group>
            </form>
        </Paper>
    );
};

export default StrategyForm;